import { Link, redirect, useLoaderData, useParams, useRouteLoaderData, useSearchParams, type ActionFunctionArgs, type LoaderFunctionArgs } from "react-router";
import { barLength, Loading, TableSkeleton } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { api, isApiError } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { saveFile, zip } from "../../lib/download.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { ArrowLeftIcon, ArrowRightIcon, Button, Card, LinkButton, PageHeader, PlusIcon, Skeleton, StatusBadge, Table } from "../../ui/index.js";
import { DecisionForm, DecisionFormSkeleton } from "./decision.jsx";
import type { workspaceLayoutLoader } from "./layout.jsx";

async function exportAll(workspaceId: string, slug: string) {
  const bundle = await api.workspace(workspaceId).exportDecisions();
  saveFile(`${slug}-decisions.zip`, zip(bundle.files), "application/zip");
}

export function decisionsLoader({ context, request }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  const cursor = new URL(request.url).searchParams.get("cursor") ?? undefined;
  return { cursor, page: deferred(context.get(apiContext).workspace(ws.id).listDecisions(cursor)) };
}

function DecisionsHead() {
  const { t } = useI18n();
  return (
    <thead>
      <tr>
        <th className="dt-nowrap">{t("decisions.th.id")}</th>
        <th style={{ width: "60%" }}>{t("decisions.th.title")}</th>
        <th className="dt-nowrap">{t("decisions.th.status")}</th>
        <th>{t("decisions.th.scope")}</th>
      </tr>
    </thead>
  );
}

function DecisionsTableSkeleton() {
  return (
    <Loading>
      <Card padded={false}>
        <TableSkeleton
          head={<DecisionsHead />}
          count={8}
          cells={(i) => [<Skeleton width={52} />, <Skeleton width={barLength(i)} />, <Skeleton shape="badge" width={72} />, <Skeleton width={80} />]}
        />
      </Card>
    </Loading>
  );
}

export function DecisionsSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader
        title={t("decisions.title")}
        description={t("decisions.description")}
        actions={
          <>
            <Skeleton shape="button" width={104} />
            <Skeleton shape="button" width={88} />
            <Skeleton shape="button" width={112} />
          </>
        }
      />
      <DecisionsTableSkeleton />
    </>
  );
}

export function DecisionsRoute() {
  const { cursor, page } = useLoaderData<typeof decisionsLoader>();
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t } = useI18n();
  const base = `/w/${workspace.slug}`;
  return (
    <>
      <PageHeader
        title={t("decisions.title")}
        description={t("decisions.description")}
        actions={
          <>
            <Button variant="ghost" onClick={() => void exportAll(workspace.id, workspace.slug)}>
              {t("decisions.exportAll")}
            </Button>
            <LinkButton to={`${base}/decisions/import`} variant="secondary">
              {t("decisions.import")}
            </LinkButton>
            <LinkButton to={`${base}/decisions/new`} variant="primary" icon={<PlusIcon />}>
              {t("decisions.draft")}
            </LinkButton>
          </>
        }
      />
      {/* Keyed by page, so the next page shows as loading instead of holding the old one. */}
      <Deferred key={cursor ?? ""} data={page} fallback={<DecisionsTableSkeleton />}>
        {(page) =>
          page.items.length === 0 ? (
            <EmptyState
              title={t("decisions.empty.title")}
              hint={t("decisions.empty.hint")}
              action={
                <LinkButton to={`${base}/decisions/new`} variant="secondary" size="sm" icon={<PlusIcon />}>
                  {t("decisions.draft")}
                </LinkButton>
              }
            />
          ) : (
            <Card padded={false}>
              <Table>
                <DecisionsHead />
                <tbody>
                  {page.items.map((d) => (
                    <tr key={d.id}>
                      <td className="dt-nowrap">
                        <Link to={`${base}/decisions/${d.id}`} className="dt-mono">
                          {d.id}
                        </Link>
                      </td>
                      <td className="dt-table__primary">
                        <Link to={`${base}/decisions/${d.id}`} style={{ color: "inherit" }}>
                          {d.title}
                        </Link>
                      </td>
                      <td className="dt-nowrap">
                        <StatusBadge status={d.status} />
                      </td>
                      <td className="dt-muted dt-nowrap">{d.scope || t("common.none")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {page.nextCursor && (
                <div className="dt-table-footer">
                  <LinkButton to={`?cursor=${encodeURIComponent(page.nextCursor)}`} variant="secondary" size="sm">
                    {t("decisions.nextPage")} <ArrowRightIcon />
                  </LinkButton>
                </div>
              )}
            </Card>
          )
        }
      </Deferred>
    </>
  );
}

export async function createDecisionAction({ context, request }: ActionFunctionArgs) {
  const ws = context.get(workspaceContext);
  const form = await request.formData();
  const field = (k: string) => String(form.get(k) ?? "").trim();
  try {
    const ids = (k: string) => field(k).split(/[\s,]+/).filter(Boolean);
    const created = await context.get(apiContext).workspace(ws.id).createDecision({
      title: field("title"),
      scope: field("scope"),
      context: field("context"),
      problem: field("problem"),
      outcome: field("outcome"),
      rationale: field("rationale"),
      ...(ids("supersedes").length ? { supersedes: ids("supersedes") } : {}),
      ...(ids("dependsOn").length ? { dependsOn: ids("dependsOn") } : {}),
    });
    return redirect(`/w/${ws.slug}/decisions/${created.id}`);
  } catch (err) {
    if (isApiError(err, "validation_failed")) return { error: err.message };
    throw err;
  }
}

function DecisionNewHeader() {
  const { workspaceSlug } = useParams();
  const [params] = useSearchParams();
  const { t } = useI18n();
  const supersedes = params.get("supersedes");
  return (
    <PageHeader
      eyebrow={
        <Link to={`/w/${workspaceSlug}/decisions`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
          <ArrowLeftIcon /> {t("decisions.title")}
        </Link>
      }
      title={supersedes ? t("decisionNew.successorTitle", { id: supersedes }) : t("decisionNew.title")}
      description={t("decisionNew.description")}
    />
  );
}

export function DecisionNewSkeleton() {
  return (
    <>
      <DecisionNewHeader />
      <Card>
        <DecisionFormSkeleton />
      </Card>
    </>
  );
}

export function DecisionNewRoute() {
  const [params] = useSearchParams();
  const { t } = useI18n();
  return (
    <>
      <DecisionNewHeader />
      <Card>
        <DecisionForm supersedes={params.get("supersedes") ?? undefined} submitLabel={t("decisionNew.submit")} />
      </Card>
    </>
  );
}
